/**
 * Resolving a stored user id back to a person's name, for the places that
 * summarise a handoff ladder rather than edit one.
 *
 * Its own module so both callers can share it: a file that exports a hook
 * alongside components loses fast refresh (the same reason `step-registry.ts`
 * exists next to the call-flow palette), and this is now read by the Iris
 * Handoff editor AND by the call flow's Iris step panel.
 */
import { useMemo } from 'react';
import { useUsers } from '@/hooks/settings/useUsers';

export function userDisplayName(u: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  id: string;
}): string {
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return u.displayName?.trim() || full || u.email || u.id;
}

/** Resolves a stored user id back to a name for a collapsed summary row. */
export function useUserNameLookup(): (userId: string) => string {
  const { data: users } = useUsers();
  return useMemo(() => {
    const byId = new Map((users?.data ?? []).map((u) => [u.id, userDisplayName(u)]));
    // A rule can outlive the person it names — showing the raw id would read as
    // corruption, so say what actually happened instead.
    return (id: string) => byId.get(id) ?? 'a removed user';
  }, [users]);
}
